/**
 * Full Risk Disclaimer Page — ProfitSpot
 * Comprehensive legal and risk disclosure
 */
import React from 'react';
import { AlertTriangle, Shield, Scale, FileText, AlertCircle, Zap, TrendingDown, Lock, ExternalLink } from 'lucide-react';

const Section = ({ icon: Icon, title, children, variant = 'default' }) => (
  <div className={`rounded-xl p-6 mb-6 ${
    variant === 'warning' 
      ? 'bg-[#ffa502]/5 border border-[#ffa502]/20' 
      : variant === 'danger'
      ? 'bg-red-500/5 border border-red-500/20'
      : 'bg-[#0f0f1a] border border-gray-800'
  }`}>
    <div className="flex items-center gap-3 mb-4">
      <Icon className={`w-6 h-6 flex-shrink-0 ${
        variant === 'warning' ? 'text-[#ffa502]' : variant === 'danger' ? 'text-red-400' : 'text-[#00ff88]'
      }`} />
      <h2 className="text-lg font-bold text-white">{title}</h2>
    </div>
    <div className="text-sm text-gray-300 leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-[#0a0a12] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#ffa502]/10 border border-[#ffa502]/20 mb-4">
            <AlertTriangle className="w-8 h-8 text-[#ffa502]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Risk Disclaimer</h1>
          <p className="text-gray-400">Please read carefully before using ProfitSpot</p>
          <p className="text-xs text-gray-600 mt-2">Last updated: March 2026</p>
        </div>

        {/* Important Notice */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-red-400 mb-2">Important Notice</h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                <strong className="text-white">Cryptocurrency and DeFi investments carry substantial risk of loss.</strong> The 
                information provided by ProfitSpot is for informational purposes only and should not be construed as 
                financial, investment, legal, or tax advice. You could lose some or all of your invested capital.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1: General Disclaimer */}
        <Section icon={FileText} title="1. General Disclaimer">
          <p>
            ProfitSpot ("the Platform," "we," "us," or "our") is a non-custodial yield intelligence platform that 
            aggregates and displays data from third-party decentralized finance (DeFi) protocols. We do not:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
            <li>Hold, manage, or have access to your cryptocurrency assets</li>
            <li>Execute transactions on your behalf</li>
            <li>Provide personalized investment advice</li>
            <li>Guarantee the accuracy, completeness, or timeliness of displayed data</li>
            <li>Endorse or recommend any specific protocol, token, or investment strategy</li>
          </ul>
          <p className="mt-3">
            All investment decisions are made solely by you. You are responsible for conducting your own due 
            diligence and understanding the risks involved before interacting with any DeFi protocol.
          </p>
        </Section>

        {/* Section 2: DeFi & Smart Contract Risks */}
        <Section icon={Lock} title="2. Smart Contract & Protocol Risks" variant="danger">
          <p>
            DeFi protocols operate on blockchain-based smart contracts that carry inherent risks:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-400 ml-2 mt-3">
            <li>
              <strong className="text-white">Exploits & Hacks:</strong> Smart contracts may contain bugs or 
              vulnerabilities that can be exploited, resulting in partial or total loss of funds.
            </li>
            <li>
              <strong className="text-white">Audit Limitations:</strong> Even audited protocols have been 
              hacked. An audit does not guarantee security.
            </li>
            <li>
              <strong className="text-white">Rug Pulls:</strong> Malicious actors may create protocols with 
              the intent to steal user funds.
            </li>
            <li>
              <strong className="text-white">Oracle Failures:</strong> Price feeds can malfunction or be 
              manipulated, causing cascading liquidations.
            </li>
            <li>
              <strong className="text-white">Governance Attacks:</strong> Protocol governance can be 
              compromised, leading to malicious changes.
            </li>
          </ul>
        </Section>

        {/* Section 3: Market Risks */}
        <Section icon={TrendingDown} title="3. Market & Financial Risks" variant="warning">
          <p>
            Cryptocurrency markets are highly volatile and unpredictable:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-400 ml-2 mt-3">
            <li>
              <strong className="text-white">Impermanent Loss (IL):</strong> Providing liquidity to 
              automated market makers (AMMs) exposes you to IL when token prices diverge. High APY may 
              not compensate for IL in volatile markets.
            </li>
            <li>
              <strong className="text-white">Price Volatility:</strong> Token values can drop 
              significantly in short periods, potentially resulting in losses even with positive yield.
            </li>
            <li>
              <strong className="text-white">Liquidity Risk:</strong> You may not be able to exit 
              positions at desired prices, especially during market stress.
            </li>
            <li>
              <strong className="text-white">Gas Costs:</strong> Transaction fees on blockchains like 
              Ethereum can be substantial, potentially exceeding your yield on smaller positions.
            </li>
            <li>
              <strong className="text-white">Yield Fluctuation:</strong> Advertised APYs are variable 
              and can decrease significantly over time.
            </li>
          </ul>
        </Section>

        {/* Section 4: Data Accuracy */}
        <Section icon={Zap} title="4. Data Accuracy & Limitations">
          <p>
            ProfitSpot aggregates data from various third-party sources including DeFiLlama, GoPlus Security, 
            and public blockchain RPCs. While we strive for accuracy:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-400 ml-2 mt-3">
            <li>Data may be delayed, incomplete, or inaccurate</li>
            <li>Safety Scores are algorithmic assessments, not guarantees</li>
            <li>APY calculations are estimates based on current conditions</li>
            <li>Historical performance does not predict future results</li>
            <li>Third-party data sources may have their own limitations</li>
          </ul>
          <p className="mt-3 text-[#ffa502]">
            Always verify information directly with the underlying protocol before investing.
          </p>
        </Section>

        {/* Section 5: Regulatory */}
        <Section icon={Scale} title="5. Regulatory Considerations">
          <p>
            The regulatory landscape for cryptocurrency and DeFi varies by jurisdiction and is evolving:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-400 ml-2 mt-3">
            <li>
              DeFi activities may be subject to taxation in your jurisdiction. You are responsible for 
              understanding and complying with all applicable tax obligations.
            </li>
            <li>
              Some DeFi protocols or tokens may be restricted or prohibited in certain jurisdictions.
            </li>
            <li>
              Regulatory actions could impact the value or accessibility of your investments.
            </li>
            <li>
              ProfitSpot does not provide legal or tax advice. Consult qualified professionals for 
              guidance specific to your situation.
            </li>
          </ul>
        </Section>

        {/* Section 6: No Warranty */}
        <Section icon={Shield} title="6. Disclaimer of Warranties">
          <p>
            THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
            FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p className="mt-3">
            We do not warrant that:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2 mt-2">
            <li>The Platform will be uninterrupted, timely, secure, or error-free</li>
            <li>The results obtained from using the Platform will be accurate or reliable</li>
            <li>Any errors in the Platform will be corrected</li>
          </ul>
        </Section>

        {/* Section 7: Limitation of Liability */}
        <Section icon={FileText} title="7. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROFITSPOT AND ITS AFFILIATES, OFFICERS, DIRECTORS, 
            EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
            OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE 
            LOSSES, RESULTING FROM:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2 mt-3">
            <li>Your use or inability to use the Platform</li>
            <li>Any unauthorized access to or use of our servers</li>
            <li>Any bugs, viruses, or similar issues transmitted through the Platform</li>
            <li>Any errors or omissions in any content</li>
            <li>Your reliance on information obtained through the Platform</li>
            <li>Investment losses of any kind</li>
          </ul>
        </Section>

        {/* Acknowledgment */}
        <div className="bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-xl p-6 mt-8">
          <h3 className="text-lg font-bold text-[#00ff88] mb-3">Your Acknowledgment</h3>
          <p className="text-sm text-gray-300 leading-relaxed">
            By using ProfitSpot, you acknowledge that you have read, understood, and agree to this Risk 
            Disclaimer. You confirm that you are aware of the risks associated with cryptocurrency and 
            DeFi investments, and that you are solely responsible for your investment decisions and any 
            resulting gains or losses.
          </p>
        </div>

        {/* Footer Links */}
        <div className="mt-10 text-center space-y-4">
          <div className="flex items-center justify-center gap-6">
            <a href="/terms" className="text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1">
              <FileText className="w-4 h-4" /> Terms of Service
            </a>
            <a href="/privacy" className="text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1">
              <Shield className="w-4 h-4" /> Privacy Policy
            </a>
          </div>
          <a 
            href="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00ff88]/10 border border-[#00ff88]/20 text-[#00ff88] rounded-lg text-sm font-medium hover:bg-[#00ff88]/20 transition-colors"
          >
            Back to ProfitSpot
          </a>
        </div>

        {/* Copyright */}
        <p className="text-center text-xs text-gray-700 mt-8">
          © 2026 ProfitSpot. Non-Custodial DeFi Intelligence.
        </p>
      </div>
    </div>
  );
}
